import * as Sentry from '@sentry/node';
import { PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import {
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  PaymentRequestShippingAddress,
  PaymentRequestShippingAddressEvent,
  PaymentRequestShippingOptionEvent,
  PaymentRequestUpdateDetails,
} from '@stripe/stripe-js';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import Icons from '@jpp/atoms/Icon';
import useToggle from '@jpp/hooks/useToggle';
import Alert, { EAlertType } from '@jpp/molecules/Alert/Alert';
import { getDeliveryMethodDescription } from '@jpp/organisms/CheckoutForm/components/ShippingMethodsFormFields/utils';
import { handleOrderCreation } from '@jpp/organisms/CheckoutForm/services';
import SubmittingPaymentModal from '@jpp/organisms/Modal/SubmittingPaymentModal';
import { IAuthStoreState } from 'common/redux/auth/reducer';
import { IIntentResponse, IPaymentIntentAPI } from 'common/redux/payment/types';
import { cancelOrder, processOrder } from 'common/redux/rootActions';
import {
  ICartCoupon,
  ICartLineItem,
  ICartShippingMethod,
  ICartStoreState,
  ICartTotals,
} from 'common/redux/wooCommerce/cart/reducer';
import { EPageType } from 'common/typings/enums';
import axios from 'common/utils/axios/cart';
import defaultAxios from 'common/utils/axios/internal';
import { SITE_AUTHOR } from 'common/utils/constants';
import {
  GHBC_BASKET_ID,
  GHBC_DEFAULT,
  GHBC_UUID,
} from 'common/utils/constants/cookies';
import { STRIPE_MULTIPLIER } from 'common/utils/constants/stripe';
import { UK_POST_CODE_REGEX } from 'common/utils/shared';
import {
  hasOnlyVirtualProductsInCart,
  hasSubscriptionProducts,
} from 'common/utils/shared/cart';
import {
  ISubmitOrderBilling,
  ISubmitOrderShipping,
  ISubmitOrderShippingLine,
} from 'common/utils/shared/orders';
import {
  getStripePrice,
  getStripeSubtotal,
  stripeCartLineItem,
} from 'common/utils/shared/stripe';

import { IWithStripeElements } from '../../../hoc/withStripe';
import './PaymentRequestButton.scss';
import { isApplePay } from './utils';

export interface IPaymentRequestButtonProps {
  className?: string;
}

export interface IStorePaymentRequestButtonProps {
  totalItems: number;
  cartLineItems: ICartLineItem[];
  shippingMethods: ICartShippingMethod[];
  coupons: ICartCoupon[];
  paymentGateway: Core.IPaymentGateway;
  user: IAuthStoreState;
}

const ROOT_CLASSNAME = 'PaymentRequestButton';

type TPaymentRequestButton = IPaymentRequestButtonProps &
  IStorePaymentRequestButtonProps &
  IWithStripeElements;

export enum EStripePaymentRequestStatus {
  Success = 'success',
  Invalid = 'invalid_shipping_address',
  Fail = 'fail',
}

export enum EStripePaymentIntentStatus {
  RequestAction = 'requires_action',
}

const PaymentRequestButton: React.FunctionComponent<TPaymentRequestButton> = ({
  className,
  stripe,
  cartLineItems,
  coupons,
  paymentGateway,
  shippingMethods,
  totalItems,
  user,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const hasSubscriptionProds = hasSubscriptionProducts(cartLineItems);
  const [isModalVisible, toggleIsModalVisible] = useToggle(false);
  const hasOnlyVirtualProducts = hasOnlyVirtualProductsInCart(cartLineItems);
  const [paymentRequest, setPaymentRequest] =
    React.useState<PaymentRequest | null>(null);
  const [error, setError] = React.useState({
    isVisible: false,
    message: '',
  });

  const isBasketShippable = !hasOnlyVirtualProductsInCart(cartLineItems);

  const stripeFailed = { status: EStripePaymentRequestStatus.Fail };
  const stripeInvalid = { status: EStripePaymentRequestStatus.Invalid };

  const stripeUpdate = (
    method: ICartShippingMethod,
    methods: ICartShippingMethod[],
    newAmount: number
  ): PaymentRequestUpdateDetails => {
    const shippingTax = parseFloat(
      Object.values(method.taxes)
        .reduce((a, b) => a + b, 0)
        .toFixed(2)
    );
    return {
      status: EStripePaymentRequestStatus.Success,
      total: {
        label: SITE_AUTHOR,
        amount:
          newAmount + getStripePrice(parseFloat(method.cost) + shippingTax),
        pending: false,
      },
      shippingOptions: methods.map(({ label, cost, method_id, taxes }) => {
        const tax = parseFloat(
          Object.values(taxes)
            .reduce((a, b) => a + b, 0)
            .toFixed(2)
        );
        return {
          label,
          amount: getStripePrice(parseFloat(cost) + tax),
          id: method_id,
          detail: getDeliveryMethodDescription(method_id),
        };
      }),
    };
  };

  const onShippingAddressChange = async (
    stripeRequest: PaymentRequestShippingAddressEvent
  ) => {
    try {
      const { postalCode, country } = stripeRequest.shippingAddress;
      if (!postalCode || !country) {
        stripeRequest.updateWith(stripeInvalid);
        return;
      }

      const postcode = UK_POST_CODE_REGEX.test(postalCode.trim())
        ? postalCode
        : `${postalCode.trim()}0AA`;
      const { data: shipping } = await axios.post<object>(
        '/calculate/shipping',
        {
          country,
          postcode,
          return_methods: true,
        }
      );

      const { data: totals } = await axios.get<ICartTotals>(
        '/totals?html=false'
      );
      const fetchedShippingMethods = Object.values(shipping);
      if (fetchedShippingMethods.length === 0) {
        stripeRequest.updateWith(stripeInvalid);
        return;
      }

      const updateWith = stripeUpdate(
        fetchedShippingMethods[0],
        fetchedShippingMethods,
        getStripeSubtotal(totals)
      );
      stripeRequest.updateWith(updateWith);
    } catch (e) {
      Sentry.captureException(e);
      stripeRequest.updateWith(stripeFailed);
    }
  };

  const onShippingOptionChange = async (
    stripeRequest: PaymentRequestShippingOptionEvent
  ) => {
    if (!shippingMethods || shippingMethods.length === 0) {
      return;
    }
    const stripeSelectedId = stripeRequest.shippingOption.id;
    const selected = shippingMethods.find(
      (method) => method.method_id === stripeSelectedId
    );
    if (selected) {
      const { data: totals } = await axios.get<ICartTotals>(
        '/totals?html=false'
      );
      stripeRequest.updateWith(
        stripeUpdate(selected, shippingMethods, getStripeSubtotal(totals))
      );
    }
  };

  const onCancel = () => void 0;

  const onPaymentMethod = async (
    paymentMethodResponse: PaymentRequestPaymentMethodEvent
  ) => {
    const {
      payerEmail,
      payerName,
      shippingAddress,
      payerPhone,
      shippingOption,
      paymentMethod,
      complete,
    } = paymentMethodResponse;

    if (!isModalVisible) {
      toggleIsModalVisible();
    }

    try {
      const convertedShippingMethod: ISubmitOrderShippingLine = {
        method_id: shippingOption?.id!,
        method_title: shippingOption?.label!,
        total: String(shippingOption?.amount! / STRIPE_MULTIPLIER),
        taxes: {},
      };
      const [first_name, last_name] = payerName!.split(' ');
      const { city, country, phone, postalCode, addressLine } =
        shippingAddress as PaymentRequestShippingAddress;

      const { data: updatedCart } = await axios.get<ICartStoreState>(
        '/get-cart'
      );
      const updatedLineItems = updatedCart.items;

      const defaultPhone = payerPhone || phone || '00000000';

      const address = {
        first_name,
        last_name,
        address_1: addressLine?.[0],
        address_2: addressLine?.[1],
        email: payerEmail,
        postcode: postalCode,
        phone: defaultPhone,
        country,
        city,
      } as ISubmitOrderShipping;

      const billing: ISubmitOrderBilling = {
        first_name:
          paymentMethod.billing_details.name?.split(' ')[0] || first_name,
        last_name:
          paymentMethod.billing_details.name?.split(' ')[1] || last_name,
        address_1: paymentMethod.billing_details.address?.line1!,
        address_2: paymentMethod.billing_details.address?.line2 || undefined,
        email: paymentMethod.billing_details.email!,
        postcode: paymentMethod.billing_details.address?.postal_code!,
        phone: paymentMethod.billing_details.phone!,
        country: paymentMethod.billing_details.address?.country!,
        city: paymentMethod.billing_details.address?.city!,
      };

      const shipping_lines = isBasketShippable
        ? [
            {
              ...convertedShippingMethod,
              total: (parseFloat(convertedShippingMethod.total) / 1.2).toFixed(
                2
              ),
            },
          ]
        : undefined;

      const { id: orderId } = await handleOrderCreation({
        shipping_lines,
        customer_id: user.id,
        billing,
        shipping: isBasketShippable ? address : undefined,
        coupon_lines: coupons.map((coupon) => ({ code: coupon?.coupon })),
        line_items: updatedLineItems.map(({ product_id, quantity }) => ({
          product_id,
          quantity,
        })),
        set_paid: false,
        payment_method: paymentGateway?.id,
        payment_method_title: paymentGateway?.method_title,
      });

      const data: IPaymentIntentAPI = {
        line_items: stripeCartLineItem(
          updatedLineItems,
          convertedShippingMethod
        ),
        order_id: orderId,
        customer_email: payerEmail!,
        customer_name: payerName!,
        customer_phone: defaultPhone,
        shipping: hasOnlyVirtualProducts ? undefined : address,
        metadata: {
          checkout_type: isApplePay() ? 'Apple Pay' : 'Google Pay',
        },
      };

      const { data: paymentIntentData } =
        await defaultAxios.post<IIntentResponse>('/payment/intent', data);
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(
          paymentIntentData.client_secret,
          { payment_method: paymentMethod.id },
          { handleActions: false }
        );

      const basketId = Cookies.get(GHBC_BASKET_ID);
      const uuid = Cookies.get(GHBC_UUID);
      const basketUser = basketId || uuid || GHBC_DEFAULT;
      const successUrl = `/${EPageType.Checkout}/${EPageType.OrderConfirmation}?order_id_success=${orderId}&user=${basketUser}&session_id=stripe_order`;

      if (confirmError) {
        await dispatch(cancelOrder(orderId));
        toggleIsModalVisible();
        complete(EStripePaymentRequestStatus.Fail);
        setError({
          isVisible: true,
          message: 'Error processing payment.',
        });
        return;
      } else {
        if (
          paymentIntent?.status === EStripePaymentIntentStatus.RequestAction
        ) {
          const { error: cardError } = await stripe.confirmCardPayment(
            paymentIntentData.client_secret
          );
          if (cardError) {
            await dispatch(cancelOrder(orderId));
            toggleIsModalVisible();
            complete(EStripePaymentRequestStatus.Fail);
            setError({
              isVisible: true,
              message:
                'There was an error with your payment method please try again or use another payment method.',
            });
            return;
          } else {
            await dispatch(processOrder(orderId));
            complete(EStripePaymentRequestStatus.Success);
            router.push(successUrl);
            return;
          }
        } else {
          await dispatch(processOrder(orderId));
          complete(EStripePaymentRequestStatus.Success);
          router.push(successUrl);
          return;
        }
      }
    } catch (e) {
      Sentry.captureException(e);
      toggleIsModalVisible();
      complete(EStripePaymentRequestStatus.Fail);
      const errMessage =
        'Failed to create order. Please use another payment method.';
      setError({ isVisible: true, message: errMessage });
      return;
    }
  };

  // @ts-ignore
  // const onSource = async (paymentRequestSource: PaymentRequestSourceEvent) => {
  // };

  const createPaymentRequest = async (shouldGetNewPaymentRequest = false) => {
    try {
      if (!paymentRequest || shouldGetNewPaymentRequest) {
        const { data: totals } = await axios.get<ICartTotals>(
          '/totals?html=false'
        );
        const pRequest = stripe.paymentRequest({
          country: 'GB',
          currency: 'gbp',
          total: {
            label: SITE_AUTHOR,
            amount: getStripeSubtotal(totals),
            pending: false,
          },
          requestPayerPhone: true,
          requestPayerName: true,
          requestPayerEmail: true,
          requestShipping: isBasketShippable,
        });

        // Check the availability of the Payment Request API.
        const canMakePayments = await pRequest.canMakePayment();
        if (!canMakePayments) {
          return;
        }

        pRequest.on('shippingaddresschange', onShippingAddressChange);
        pRequest.on('shippingoptionchange', onShippingOptionChange);
        pRequest.on('paymentmethod', onPaymentMethod);
        // pRequest.on('source', onSource);
        pRequest.on('cancel', onCancel);
        setPaymentRequest(pRequest);
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const showGooglePay = () => {
    if (!paymentRequest) {
      return;
    }
    paymentRequest.show();
  };

  const updatePaymentRequest = async () => {
    if (!paymentRequest) {
      return;
    }
    try {
      const { data: totals } = await axios.get<ICartTotals>(
        '/totals?html=false'
      );
      paymentRequest.update({
        currency: 'gbp',
        total: {
          label: SITE_AUTHOR,
          amount: getStripeSubtotal(totals),
          pending: true,
        },
      });
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  useEffect(() => {
    if (stripe) {
      createPaymentRequest();
    }
  }, [stripe]);

  useEffect(() => {
    createPaymentRequest(true);
  }, [totalItems, coupons]);

  if (coupons.length > 0 || hasSubscriptionProds || !paymentRequest) {
    return null;
  }

  return (
    <>
      <div className={classNames(ROOT_CLASSNAME, className)}>
        {error.isVisible && (
          <Alert
            isVisible={true}
            className={`${ROOT_CLASSNAME}__error-alert`}
            alertType={EAlertType.Error}
            children={error.message}
          />
        )}

        {isApplePay() ? (
          <PaymentRequestButtonElement
            onReady={() => updatePaymentRequest()}
            options={{
              paymentRequest,
              style: { paymentRequestButton: { height: '44px' } },
            }}
          />
        ) : (
          <button
            type="button"
            className={`${ROOT_CLASSNAME}__google-pay`}
            onClick={showGooglePay}
          >
            <Icons.GooglePay className={`${ROOT_CLASSNAME}__icon`} />
            <span className={`${ROOT_CLASSNAME}__text`}>Google Pay</span>
          </button>
        )}
      </div>

      {isModalVisible && (
        <SubmittingPaymentModal
          bodyOverflow={false}
          isVisible={isModalVisible}
          onClose={toggleIsModalVisible}
        />
      )}
    </>
  );
};

export default PaymentRequestButton;
